import React, { useState, useCallback, useMemo } from 'react'
import {
    Box,
    Typography,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Paper,
    makeStyles,
    CircularProgress,
    TableSortLabel,
    IconButton,
    useTheme,
} from '@material-ui/core'
import { Pagination } from '@material-ui/lab'
import {
    AttachMoney,
    Schedule,
    Person,
    FilterList,
    Send,
} from '@material-ui/icons'
import { toast } from 'react-toastify'

import { useRent, ValidTenantBillSortField } from '../../hooks/useRent'
import { TenantBill } from '../../models/Rent/types'
import TenantBillFilterDrawer from './TenantBillFilterDrawer'
import TenantBillFilterChips from './TenantBillFilterChips'
import { TenantBillDetailModal } from './TenantBillDetailModal'

const useStyles = makeStyles((theme) => ({
    container: {
        padding: theme.spacing(1),
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
    },
    tableBox: {
        flex: 1,
        position: 'relative',
        marginBottom: theme.spacing(8),
        overflow: 'hidden',
    },
    header: {
        marginBottom: theme.spacing(2),
        flexShrink: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    headerContent: {
        flex: 1,
    },
    filterButton: {
        marginLeft: theme.spacing(2),
    },

    statusChip: {
        marginRight: theme.spacing(1),
    },
    paginationContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(2),
        flexShrink: 0,
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        minHeight: '80px',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 10,
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(4),
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(4),
        color: theme.palette.text.secondary,
    },
    amountCell: {
        fontWeight: 500,
    },
    overdueAmount: {
        color: theme.palette.error.main,
        fontWeight: 500,
    },
    // Memoized styles to avoid inline object creation
    tenantCell: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    billIdCell: {
        fontFamily: 'monospace',
    },
    tenantName: {
        fontWeight: 500,
    },
    dueDateCell: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    actionCell: {
        display: 'flex',
        gap: 8,
    },
    sortableHeader: {
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    activeSortHeader: {
        backgroundColor: theme.palette.action.selected,
        fontWeight: 600,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(0.5, 1),
    },
    voidedRow: {
        textDecoration: 'line-through',
        opacity: 0.6,
        backgroundColor: theme.palette.grey[100],
    },
}))

interface TenantBillTabProps {
    apartmentId?: number
}

// Filter interface
interface TenantBillFilters {
    status?: string
    search?: string
    dueDateStart?: string
    dueDateEnd?: string
    minAmount?: string
    maxAmount?: string
    billingMonth?: string
    paymentStatus?: string
    isOverdue?: string
    minAmountPaid?: string
    maxAmountPaid?: string
    minBalanceDue?: string
    maxBalanceDue?: string
    createdDateStart?: string
    createdDateEnd?: string
}

export const TenantBillTab: React.FC<TenantBillTabProps> = ({
    apartmentId,
}) => {
    const classes = useStyles()
    const { useTenantBills, sendOverdueNotice } = useRent()
    const theme = useTheme()
    const [sendingReminder, setSendingReminder] = useState<number | null>(null)

    const handleSendReminder = async (tenantId: number) => {
        try {
            setSendingReminder(tenantId)
            await sendOverdueNotice({ tenant_id: tenantId })
            toast.success('Reminder sent successfully')
        } catch (error: any) {
            console.error('Error sending reminder:', error)
            const errorMessage =
                error.response?.data?.message || 'Failed to send reminder'
            toast.error(errorMessage)
        } finally {
            setSendingReminder(null)
        }
    }

    // Filter drawer state
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)

    // Modal state
    const [selectedBill, setSelectedBill] = useState<TenantBill | null>(null)
    const [modalOpen, setModalOpen] = useState(false)

    // Filter values
    const [filters, setFilters] = useState<TenantBillFilters>({})
    const [appliedFilters, setAppliedFilters] = useState<TenantBillFilters>({})

    const [page, setPage] = useState(1)
    const [pageSize] = useState(50)
    const [sortField, setSortField] = useState<ValidTenantBillSortField>(
        '-due_date',
    )

    const params = useMemo(
        () => ({
            page,
            page_size: pageSize,
            sort: sortField,
            ...(apartmentId && { apartment_id: apartmentId }),
            ...(appliedFilters.status && { status: appliedFilters.status }),
            ...(appliedFilters.search && { search: appliedFilters.search }),
            ...(appliedFilters.paymentStatus && {
                payment_status: appliedFilters.paymentStatus,
            }),
            ...(appliedFilters.isOverdue && {
                is_overdue: appliedFilters.isOverdue,
            }),
            ...(appliedFilters.dueDateStart && {
                due_date_start: appliedFilters.dueDateStart,
            }),
            ...(appliedFilters.dueDateEnd && {
                due_date_end: appliedFilters.dueDateEnd,
            }),
            ...(appliedFilters.createdDateStart && {
                created_date_start: appliedFilters.createdDateStart,
            }),
            ...(appliedFilters.createdDateEnd && {
                created_date_end: appliedFilters.createdDateEnd,
            }),
            ...(appliedFilters.minAmount && {
                min_amount: appliedFilters.minAmount,
            }),
            ...(appliedFilters.maxAmount && {
                max_amount: appliedFilters.maxAmount,
            }),
            ...(appliedFilters.minAmountPaid && {
                min_amount_paid: appliedFilters.minAmountPaid,
            }),
            ...(appliedFilters.maxAmountPaid && {
                max_amount_paid: appliedFilters.maxAmountPaid,
            }),
            ...(appliedFilters.minBalanceDue && {
                min_balance_due: appliedFilters.minBalanceDue,
            }),
            ...(appliedFilters.maxBalanceDue && {
                max_balance_due: appliedFilters.maxBalanceDue,
            }),
        }),
        [page, pageSize, sortField, apartmentId, appliedFilters],
    )

    const { data, isLoading, error } = useTenantBills(params)

    const handlePageChange = useCallback(
        (event: React.ChangeEvent<unknown>, newPage: number) => {
            setPage(newPage)
        },
        [],
    )

    const handleRequestSort = useCallback(
        (property: ValidTenantBillSortField) => {
            // Extract the base field name (remove the '-' prefix if present)
            const baseField = property.startsWith('-')
                ? property.substring(1)
                : property

            // Determine if we're currently sorting by this field
            const isCurrentlySorting =
                sortField === baseField || sortField === `-${baseField}`

            // Determine the new sort field
            let newSortField: ValidTenantBillSortField
            if (isCurrentlySorting) {
                // If already sorting by this field, toggle the order
                newSortField = sortField.startsWith('-')
                    ? (baseField as ValidTenantBillSortField)
                    : (`-${baseField}` as ValidTenantBillSortField)
            } else {
                // If not currently sorting by this field, default to descending
                newSortField = `-${baseField}` as ValidTenantBillSortField
            }

            setSortField(newSortField)
            setPage(1) // Reset to first page when sorting changes
        },
        [sortField],
    )

    // Filter handlers
    const handleFilterChange = useCallback(
        (field: keyof TenantBillFilters, value: string) => {
            setFilters((prev) => ({
                ...prev,
                [field]: value,
            }))
        },
        [],
    )

    const handleApplyFilters = useCallback(() => {
        setAppliedFilters(filters)
        setPage(1) // Reset to first page when filters change
        setFilterDrawerOpen(false)
    }, [filters])

    const handleResetFilters = useCallback(() => {
        setFilters({})
        setAppliedFilters({})
        setPage(1)
        setFilterDrawerOpen(false)
    }, [])

    const handleClearFilter = useCallback(
        (field: keyof TenantBillFilters) => {
            const newFilters = { ...appliedFilters }
            delete newFilters[field]
            setAppliedFilters(newFilters)
            setFilters(newFilters)
            setPage(1)
        },
        [appliedFilters],
    )

    // Quick filter presets
    const handleQuickFilter = useCallback((preset: string) => {
        let newFilters: TenantBillFilters = {}

        switch (preset) {
            case 'overdue':
                newFilters = { isOverdue: 'true' }
                break
            case 'unpaid':
                newFilters = { paymentStatus: 'unpaid' }
                break
            case 'paid':
                newFilters = { paymentStatus: 'paid' }
                break
            case 'thisMonth': {
                const now = new Date()
                const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
                const lastDay = new Date(
                    now.getFullYear(),
                    now.getMonth() + 1,
                    0,
                )
                newFilters = {
                    dueDateStart: firstDay.toISOString().split('T')[0],
                    dueDateEnd: lastDay.toISOString().split('T')[0],
                }
                break
            }
            case 'lastMonth': {
                const lastMonth = new Date()
                lastMonth.setMonth(lastMonth.getMonth() - 1)
                const firstDayLastMonth = new Date(
                    lastMonth.getFullYear(),
                    lastMonth.getMonth(),
                    1,
                )
                const lastDayLastMonth = new Date(
                    lastMonth.getFullYear(),
                    lastMonth.getMonth() + 1,
                    0,
                )
                newFilters = {
                    dueDateStart: firstDayLastMonth.toISOString().split('T')[0],
                    dueDateEnd: lastDayLastMonth.toISOString().split('T')[0],
                }
                break
            }
            case 'thisYear': {
                const now = new Date()
                const firstDayOfYear = new Date(now.getFullYear(), 0, 1)
                const lastDayOfYear = new Date(now.getFullYear(), 11, 31)
                newFilters = {
                    dueDateStart: firstDayOfYear.toISOString().split('T')[0],
                    dueDateEnd: lastDayOfYear.toISOString().split('T')[0],
                }
                break
            }
            default:
                return
        }

        setFilters(newFilters)
        setAppliedFilters(newFilters)
        setPage(1)
        setFilterDrawerOpen(false)
    }, [])

    // Modal handlers
    const handleViewDetails = useCallback((bill: TenantBill) => {
        setSelectedBill(bill)
        setModalOpen(true)
    }, [])

    const handleCloseModal = useCallback(() => {
        setModalOpen(false)
        setSelectedBill(null)
    }, [])

    const handleBillUpdate = useCallback((updatedBill: TenantBill) => {
        setSelectedBill(updatedBill)
    }, [])

    // Count active filters
    const activeFiltersCount = Object.keys(appliedFilters).filter(
        (key) => appliedFilters[key as keyof TenantBillFilters],
    ).length

    const getStatusChip = useCallback(
        (status: string) => {
            switch (status?.toLowerCase()) {
                case 'draft':
                    return (
                        <Chip
                            label="Draft"
                            color="default"
                            size="small"
                            className={classes.statusChip}
                        />
                    )
                case 'sent':
                    return (
                        <Chip
                            label="Sent"
                            color="secondary"
                            size="small"
                            className={classes.statusChip}
                        />
                    )
                case 'paid':
                    return (
                        <Chip
                            label="Paid"
                            color="primary"
                            size="small"
                            className={classes.statusChip}
                        />
                    )
                case 'overdue':
                    return (
                        <Chip
                            label="Overdue"
                            color="default"
                            size="small"
                            className={classes.statusChip}
                        />
                    )
                case 'voided':
                    return (
                        <Chip
                            label="Voided"
                            color="default"
                            size="small"
                            className={classes.statusChip}
                        />
                    )
                case 'partial':
                    return (
                        <Chip
                            label="Partial"
                            color="default"
                            size="small"
                            className={classes.statusChip}
                        />
                    )
                case 'pending':
                    return (
                        <Chip
                            label="Pending"
                            color="default"
                            size="small"
                            className={classes.statusChip}
                        />
                    )
                default:
                    return (
                        <Chip
                            label={status || 'Unknown'}
                            color="default"
                            size="small"
                            className={classes.statusChip}
                        />
                    )
            }
        },
        [classes.statusChip],
    )

    const formatCurrency = useCallback((amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount)
    }, [])

    const formatDate = useCallback((dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        })
    }, [])

    const formatDueDate = useCallback((dateString: string) => {
        // Split YYYY-MM-DD format and create date parts
        const [year, month, day] = dateString.split('-').map(Number)
        // Month is 0-based in JS Date
        const date = new Date(year, month - 1, day)
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        })
    }, [])

    if (isLoading) {
        return (
            <Box className={classes.loadingContainer}>
                <CircularProgress />
            </Box>
        )
    }

    if (error) {
        return (
            <Box className={classes.emptyState}>
                <Typography variant="h6" color="error">
                    Error loading tenant bills
                </Typography>
                <Typography variant="body2">{error.message}</Typography>
            </Box>
        )
    }

    // Handle different possible data structures
    const billsData = data?.results || (Array.isArray(data) ? data : [])
    const totalCount = data?.count || (Array.isArray(data) ? data.length : 0)
    const totalPages = totalCount ? Math.ceil(totalCount / pageSize) : 0

    if (process.env.NODE_ENV === 'development') {
        console.log('TenantBillTab - billsData:', billsData)
        console.log('TenantBillTab - totalCount:', totalCount)
        console.log('TenantBillTab - first bill:', billsData[0])
    }

    return (
        <Box className={classes.container}>
            {/* Header */}
            <Box className={classes.header}>
                <Box className={classes.headerContent}>
                    <Typography variant="h5" component="h2" gutterBottom>
                        Tenant Bills
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Manage and track tenant billing information
                    </Typography>

                    {/* Active filters display */}
                    <TenantBillFilterChips
                        appliedFilters={appliedFilters}
                        onClearFilter={handleClearFilter}
                    />
                </Box>

                <IconButton
                    color={activeFiltersCount > 0 ? 'primary' : 'default'}
                    onClick={() => setFilterDrawerOpen(true)}
                    className={classes.filterButton}
                    title="Filter bills"
                >
                    <FilterList />
                </IconButton>
            </Box>

            {/* Table */}
            <Box className={classes.tableBox}>
                <TableContainer
                    style={{ height: 'calc(100% - 10px)', overflow: 'auto' }}
                >
                    <Table stickyHeader style={{ minWidth: 1500 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <TableSortLabel
                                        active={
                                            sortField === 'tenant_name' ||
                                            sortField === '-tenant_name'
                                        }
                                        direction={
                                            sortField === 'tenant_name'
                                                ? 'asc'
                                                : 'desc'
                                        }
                                        onClick={() =>
                                            handleRequestSort('tenant_name')
                                        }
                                        className={`${classes.sortableHeader} ${
                                            sortField === 'tenant_name' ||
                                            sortField === '-tenant_name'
                                                ? classes.activeSortHeader
                                                : ''
                                        }`}
                                    >
                                        Tenant
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={
                                            sortField === 'unit_name' ||
                                            sortField === '-unit_name'
                                        }
                                        direction={
                                            sortField === 'unit_name'
                                                ? 'asc'
                                                : 'desc'
                                        }
                                        onClick={() =>
                                            handleRequestSort('unit_name')
                                        }
                                        className={`${classes.sortableHeader} ${
                                            sortField === 'unit_name' ||
                                            sortField === '-unit_name'
                                                ? classes.activeSortHeader
                                                : ''
                                        }`}
                                    >
                                        Unit Name
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>Area</TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={
                                            sortField === 'subtotal' ||
                                            sortField === '-subtotal'
                                        }
                                        direction={
                                            sortField === 'subtotal'
                                                ? 'asc'
                                                : 'desc'
                                        }
                                        onClick={() =>
                                            handleRequestSort('subtotal')
                                        }
                                        className={`${classes.sortableHeader} ${
                                            sortField === 'subtotal' ||
                                            sortField === '-subtotal'
                                                ? classes.activeSortHeader
                                                : ''
                                        }`}
                                    >
                                        Total Amount
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>Amount Paid</TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={
                                            sortField === 'balance_due' ||
                                            sortField === '-balance_due'
                                        }
                                        direction={
                                            sortField === 'balance_due'
                                                ? 'asc'
                                                : 'desc'
                                        }
                                        onClick={() =>
                                            handleRequestSort('balance_due')
                                        }
                                        className={`${classes.sortableHeader} ${
                                            sortField === 'balance_due' ||
                                            sortField === '-balance_due'
                                                ? classes.activeSortHeader
                                                : ''
                                        }`}
                                    >
                                        Balance Due
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={
                                            sortField === 'due_date' ||
                                            sortField === '-due_date'
                                        }
                                        direction={
                                            sortField === 'due_date'
                                                ? 'asc'
                                                : 'desc'
                                        }
                                        onClick={() =>
                                            handleRequestSort('due_date')
                                        }
                                        className={`${classes.sortableHeader} ${
                                            sortField === 'due_date' ||
                                            sortField === '-due_date'
                                                ? classes.activeSortHeader
                                                : ''
                                        }`}
                                    >
                                        Due Date
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={
                                            sortField === 'status' ||
                                            sortField === '-status'
                                        }
                                        direction={
                                            sortField === 'status'
                                                ? 'asc'
                                                : 'desc'
                                        }
                                        onClick={() =>
                                            handleRequestSort('status')
                                        }
                                        className={`${classes.sortableHeader} ${
                                            sortField === 'status' ||
                                            sortField === '-status'
                                                ? classes.activeSortHeader
                                                : ''
                                        }`}
                                    >
                                        Status
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {billsData && billsData.length > 0 ? (
                                billsData.map(
                                    (bill: TenantBill, index: number) => (
                                        <TableRow
                                            key={bill.id || index}
                                            className={
                                                bill.status === 'voided'
                                                    ? classes.voidedRow
                                                    : ''
                                            }
                                        >
                                            <TableCell>
                                                <Box
                                                    className={
                                                        classes.tenantCell
                                                    }
                                                >
                                                    <Person fontSize="small" />
                                                    <Typography
                                                        variant="body2"
                                                        className={
                                                            classes.tenantName
                                                        }
                                                    >
                                                        {bill.tenant?.name ||
                                                            'Unknown Tenant'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    className={
                                                        classes.billIdCell
                                                    }
                                                >
                                                    {bill.lease?.area?.unit
                                                        ?.name || 'N/A'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {bill.lease?.area
                                                        ?.area_config?.name &&
                                                    bill.lease?.area?.area_label
                                                        ? `${bill.lease.area.area_config.name} - ${bill.lease.area.area_label}`
                                                        : 'N/A'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    className={
                                                        classes.amountCell
                                                    }
                                                >
                                                    {formatCurrency(
                                                        bill.subtotal || 0,
                                                    )}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    className={
                                                        classes.amountCell
                                                    }
                                                >
                                                    {formatCurrency(
                                                        bill.amount_paid || 0,
                                                    )}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    className={
                                                        bill.balance_due > 0
                                                            ? classes.overdueAmount
                                                            : classes.amountCell
                                                    }
                                                >
                                                    {formatCurrency(
                                                        bill.balance_due || 0,
                                                    )}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box
                                                    className={
                                                        classes.dueDateCell
                                                    }
                                                >
                                                    <Schedule fontSize="small" />
                                                    <Typography variant="body2">
                                                        {bill.due_date
                                                            ? formatDueDate(
                                                                  bill.due_date,
                                                              )
                                                            : 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                >
                                                    {getStatusChip(
                                                        bill.status ||
                                                            'unknown',
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box
                                                    className={
                                                        classes.actionCell
                                                    }
                                                >
                                                    <Chip
                                                        label="View Details"
                                                        size="small"
                                                        variant="outlined"
                                                        clickable
                                                        onClick={() =>
                                                            handleViewDetails(
                                                                bill,
                                                            )
                                                        }
                                                    />
                                                    {!bill.is_voided &&
                                                        bill.balance_due > 0 &&
                                                        (() => {
                                                            const [
                                                                year,
                                                                month,
                                                                day,
                                                            ] = bill.due_date
                                                                .split('-')
                                                                .map(Number)
                                                            return (
                                                                new Date(
                                                                    year,
                                                                    month - 1,
                                                                    day,
                                                                ) < new Date()
                                                            )
                                                        })() && (
                                                            <Chip
                                                                icon={
                                                                    <Send fontSize="small" />
                                                                }
                                                                label={
                                                                    sendingReminder ===
                                                                    bill.tenant
                                                                        ?.id
                                                                        ? 'Sending...'
                                                                        : 'Send Reminder'
                                                                }
                                                                size="small"
                                                                variant="outlined"
                                                                clickable
                                                                onClick={() =>
                                                                    bill.tenant
                                                                        ?.id &&
                                                                    handleSendReminder(
                                                                        bill
                                                                            .tenant
                                                                            .id,
                                                                    )
                                                                }
                                                                disabled={
                                                                    sendingReminder ===
                                                                    bill.tenant
                                                                        ?.id
                                                                }
                                                                style={{
                                                                    opacity:
                                                                        sendingReminder ===
                                                                        bill
                                                                            .tenant
                                                                            ?.id
                                                                            ? 0.7
                                                                            : 1,
                                                                }}
                                                            />
                                                        )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ),
                                )
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={9}>
                                        <Box className={classes.emptyState}>
                                            <AttachMoney
                                                style={{
                                                    fontSize: 48,
                                                    marginBottom: 16,
                                                }}
                                            />
                                            <Typography variant="h6">
                                                No tenant bills found
                                            </Typography>
                                            <Typography variant="body2">
                                                No bills have been created yet
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Pagination */}
            <Box className={classes.paginationContainer}>
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    variant="outlined"
                    shape="rounded"
                    disabled={totalPages <= 1}
                />
                {totalCount > 0 && (
                    <Box
                        style={{
                            marginLeft: 16,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                        }}
                    >
                        <Typography variant="body2" color="textSecondary">
                            Showing {(page - 1) * pageSize + 1} to{' '}
                            {Math.min(page * pageSize, totalCount)} of{' '}
                            {totalCount} bills
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Filter Drawer */}
            <TenantBillFilterDrawer
                open={filterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
                filters={filters}
                onFilterChange={handleFilterChange}
                onApply={handleApplyFilters}
                onReset={handleResetFilters}
                onQuickFilter={handleQuickFilter}
            />

            {/* Bill Detail Modal */}
            <TenantBillDetailModal
                open={modalOpen}
                onClose={handleCloseModal}
                bill={selectedBill}
                onBillUpdate={handleBillUpdate}
            />
        </Box>
    )
}
